/**
 * IntegrationConnectionsList Component
 *
 * Displays a list of integration connections with:
 * - Connection name and status
 * - Last tested/synced timestamps
 * - Test and delete actions
 *
 * Migrated onto the List & Grid Standard (embedded child list — `ListFrame`, not
 * `ListPageLayout`): standard `<Table>` + sortable headers, `actionsColumnWidth`,
 * client-side `Pager` (PAGE_SIZE=25) over the passed-in `connections` prop, and
 * `useListUrlState` for page/sort. See `.github/context/list-grid-standard.context.md`
 * and `.github/context/list-grid-standard-behaviours.context.md`.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  Card,
  CardHeader,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Button,
  Spinner,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  tokens,
  makeStyles,
  Tooltip,
} from '@fluentui/react-components';
import {
  PlugConnectedRegular,
  DeleteRegular,
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import type { ConnectionStatus, ConnectionTestResult } from '../types';
import { useAuth } from '../../authentication';
import {
  ListFrame,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  makeSortComparator,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
} from '../../../components/list';
import type { SortDirection, SortState } from '../../../components/list';

/** Roles allowed to manage integration connections (test / delete) — mirrors the
 *  `RequireAnyRole("Organization.Admin", "TenantAdmin", "IntegrationAdmin")` check used
 *  identically by GitHubController, GitLabController, AzureDevOpsController, JiraController,
 *  ServiceNowController, and SnykController's `TestConnection`/`DeleteConnection` actions. */
const INTEGRATION_MANAGER_ROLES = ['Organization.Admin', 'TenantAdmin', 'IntegrationAdmin'];

const useStyles = makeStyles({
  card: {
    width: '100%',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  statusConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusConfigured: {
    color: tokens.colorPaletteBlueForeground2,
  },
  statusError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusDisabled: {
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    padding: tokens.spacingVerticalM,
  },
  headerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  urlCell: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
});

/**
 * Get badge color based on connection status
 */
function getStatusBadgeColor(status: ConnectionStatus): 'success' | 'informative' | 'danger' | 'subtle' {
  switch (status) {
    case 'Connected':
      return 'success';
    case 'Configured':
      return 'informative';
    case 'Error':
      return 'danger';
    case 'Disabled':
    default:
      return 'subtle';
  }
}

/**
 * Get status icon based on connection status
 */
function getStatusIcon(status: ConnectionStatus) {
  switch (status) {
    case 'Connected':
      return <CheckmarkCircleRegular />;
    case 'Error':
      return <ErrorCircleRegular />;
    case 'Disabled':
      return <WarningRegular />;
    default:
      return null;
  }
}

/**
 * Format a date for display
 */
function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
}

export interface ConnectionItem {
  id: number;
  name: string;
  instanceUrl?: string;
  organizationUrl?: string;
  status: ConnectionStatus;
  lastError?: string;
  lastTestedAt?: string;
  lastSyncAt?: string;
  createdAt: string;
}

export interface IntegrationConnectionsListProps<T extends ConnectionItem> {
  /** Title of the integration type (e.g., "GitLab", "Jira") */
  title: string;
  /** Icon to display in the header */
  icon?: ReactElement;
  /** Connections data */
  connections?: T[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error if loading failed */
  error?: Error | null;
  /** Whether testing is in progress */
  isTestingConnection?: boolean;
  /** ID of connection currently being tested */
  testingConnectionId?: number;
  /** Whether deletion is in progress */
  isDeletingConnection?: boolean;
  /** Callback to test a connection */
  onTestConnection?: (connectionId: number) => Promise<ConnectionTestResult>;
  /** Callback to delete a connection */
  onDeleteConnection?: (connectionId: number) => Promise<void>;
  /** Callback to add a new connection */
  onAddConnection?: () => void;
  /** Custom columns to render */
  renderCustomColumns?: (connection: T) => ReactNode;
  /** Custom header cells */
  customHeaders?: ReactNode;
  /** Text for empty state */
  emptyStateText?: string;
  /** Custom content to render in the empty state (overrides emptyStateText) */
  emptyStateContent?: ReactNode;
}

export function IntegrationConnectionsList<T extends ConnectionItem>({
  title,
  icon = <PlugConnectedRegular />,
  connections,
  isLoading = false,
  error = null,
  isTestingConnection = false,
  testingConnectionId,
  isDeletingConnection = false,
  onTestConnection,
  onDeleteConnection,
  onAddConnection,
  renderCustomColumns,
  customHeaders,
  emptyStateText = 'No connections configured. Add a connection to get started.',
  emptyStateContent,
}: IntegrationConnectionsListProps<T>) {
  const styles = useStyles();
  const { user } = useAuth();
  const [connectionToDelete, setConnectionToDelete] = useState<T | null>(null);
  const [testResult, setTestResult] = useState<{ id: number; result: ConnectionTestResult } | null>(null);

  // §18 — hidden-not-disabled role gate, mirroring the API's RequireAnyRole for
  // TestConnection/DeleteConnection. An action renders only when BOTH its callback
  // prop was supplied AND the user holds one of the required roles.
  const canManageConnections = user?.roles?.some((role) => INTEGRATION_MANAGER_ROLES.includes(role)) ?? false;
  const canTest = canManageConnections && !!onTestConnection;
  const canDelete = canManageConnections && !!onDeleteConnection;
  const actionButtonCount = (canTest ? 1 : 0) + (canDelete ? 1 : 0);
  const showActionsColumn = actionButtonCount > 0;

  const { state, setPage, setSort } = useListUrlState({ defaultSort: 'name:asc' });
  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  // Client-side sort + paginate over the passed-in `connections` prop — this component
  // never fetches its own data, so there is no adaptive/server mode, just PAGE_SIZE=25
  // slicing of whatever the parent handed us.
  const connectionList = useMemo(() => connections ?? [], [connections]);

  const sortedConnections = useMemo(
    () => [...connectionList].sort(makeSortComparator<T>(sortState)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectionList, sortField, sortDir]
  );

  const totalCount = sortedConnections.length;
  const pageSize = state.pageSize;
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);
  const page = Math.min(state.page, totalPages);
  const startIndex = (page - 1) * pageSize;
  const pagedConnections = sortedConnections.slice(startIndex, startIndex + pageSize);
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;

  // Out-of-range clamp (§9) — never strand the user on an empty page after the
  // underlying connections list shrinks (e.g. a delete removes the last row on a page).
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  const handleTestConnection = async (connection: T) => {
    if (onTestConnection) {
      const result = await onTestConnection(connection.id);
      setTestResult({ id: connection.id, result });
      setTimeout(() => setTestResult(null), 5000); // Clear after 5 seconds
    }
  };

  const handleDeleteConnection = async () => {
    if (connectionToDelete && onDeleteConnection) {
      await onDeleteConnection(connectionToDelete.id);
      setConnectionToDelete(null);
    }
  };

  const renderAddButton = () => {
    if (!onAddConnection) return undefined;
    return (
      <Button appearance="primary" size="small" onClick={onAddConnection}>
        Add Connection
      </Button>
    );
  };

  if (isLoading) {
    return (
      <Card className={styles.card}>
        <CardHeader
          image={icon}
          header={<Text weight="semibold">{title}</Text>}
        />
        <div className={styles.loadingContainer}>
          <LoadingSkeleton rows={5} columns={6} />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.card}>
        <CardHeader
          image={icon}
          header={<Text weight="semibold">{title}</Text>}
        />
        <ErrorState message={error.message} />
      </Card>
    );
  }

  if (!connections || connections.length === 0) {
    return (
      <Card className={styles.card}>
        <CardHeader
          image={icon}
          header={<Text weight="semibold">{title}</Text>}
          action={renderAddButton()}
        />
        {emptyStateContent || <EmptyState icon={icon} title={emptyStateText} />}
      </Card>
    );
  }

  return (
    <>
      <Card className={styles.card}>
        <CardHeader
          image={icon}
          header={<Text weight="semibold">{title} ({totalCount})</Text>}
          action={renderAddButton()}
        />
        <ListFrame>
          <div className={styles.tableContainer}>
            <Table aria-label={`${title} connections`}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell
                    sortable
                    sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    sortIcon={sortIcon('name')}
                    onClick={() => toggleSort('name')}
                  >
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell>URL</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  {customHeaders}
                  <TableHeaderCell
                    sortable
                    sortDirection={
                      sortField === 'lastTestedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    sortIcon={sortIcon('lastTestedAt')}
                    onClick={() => toggleSort('lastTestedAt')}
                  >
                    Last Tested
                  </TableHeaderCell>
                  {showActionsColumn && (
                    <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                      Actions
                    </TableHeaderCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedConnections.map((connection) => {
                  const isCurrentlyTesting = isTestingConnection && testingConnectionId === connection.id;
                  const currentTestResult = testResult?.id === connection.id ? testResult.result : null;

                  return (
                    <TableRow key={connection.id}>
                      <TableCell>
                        <Text weight="semibold">{connection.name}</Text>
                      </TableCell>
                      <TableCell>
                        <Tooltip content={connection.instanceUrl || connection.organizationUrl || ''} relationship="label">
                          <Text className={styles.urlCell}>
                            {connection.instanceUrl || connection.organizationUrl}
                          </Text>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Badge
                          appearance="filled"
                          color={getStatusBadgeColor(connection.status)}
                          icon={getStatusIcon(connection.status)}
                        >
                          {connection.status}
                        </Badge>
                        {connection.lastError && connection.status === 'Error' && (
                          <Tooltip content={connection.lastError} relationship="description">
                            <ErrorCircleRegular style={{ marginLeft: '4px', color: tokens.colorPaletteRedForeground1 }} />
                          </Tooltip>
                        )}
                        {currentTestResult && (
                          <Badge
                            appearance="outline"
                            color={currentTestResult.success ? 'success' : 'danger'}
                            style={{ marginLeft: '8px' }}
                          >
                            {currentTestResult.success ? 'Test Passed' : 'Test Failed'}
                          </Badge>
                        )}
                      </TableCell>
                      {renderCustomColumns?.(connection)}
                      <TableCell>
                        <Text size={200}>{formatDate(connection.lastTestedAt)}</Text>
                      </TableCell>
                      {showActionsColumn && (
                        <TableCell>
                          <div className={styles.actionsCell}>
                            {canTest && (
                              <Tooltip content={isCurrentlyTesting ? 'Testing…' : 'Test Connection'} relationship="label">
                                <Button
                                  appearance="subtle"
                                  icon={isCurrentlyTesting ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                                  size="small"
                                  aria-label="Test Connection"
                                  disabled={isCurrentlyTesting}
                                  onClick={() => handleTestConnection(connection)}
                                />
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip content="Delete Connection" relationship="label">
                                <Button
                                  appearance="subtle"
                                  icon={<DeleteRegular />}
                                  size="small"
                                  aria-label="Delete Connection"
                                  onClick={() => setConnectionToDelete(connection)}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </ListFrame>
        <Pager
          page={page}
          totalPages={totalPages}
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          totalCount={totalCount}
          onPageChange={setPage}
          testId={`${title.toLowerCase().replace(/\s+/g, '-')}-connections-pager`}
        />
      </Card>

      {/* Delete Connection Confirmation Dialog */}
      <Dialog
        open={!!connectionToDelete}
        onOpenChange={(_, data) => !data.open && setConnectionToDelete(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Connection</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete the connection{' '}
                <Text weight="semibold">{connectionToDelete?.name}</Text>?
                This action cannot be undone.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConnectionToDelete(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeleteConnection}
                disabled={isDeletingConnection}
              >
                {isDeletingConnection ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

export default IntegrationConnectionsList;
